
export interface QNav {
  resetFabPos: () => void;
}

import { Objective, ObjectiveHistory, TouchPanType } from 'src/types';
import { computed, defineComponent, PropType, ref } from 'vue';

export default defineComponent({
  name: 'QuestionNavigator',
  props: {
    questions: {
      type: [Object] as PropType<Objective[]>,
      required: true,
    },
    histories: {
      type: [Object] as PropType<Partial<ObjectiveHistory>[]>,
      required: true,
    },
    isAnswerScreen: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const refHistory = ref<Partial<ObjectiveHistory>[]>(props.histories);

    const isHistory = ref(props.isAnswerScreen);
    const draggingFab = ref(false);
    const disabled = ref(false);
    const fabPos = ref([50, 75]);

    const resetFabPos = () => {
      fabPos.value = [50, 75];
    };

    const getIconName = (question: Objective) => {
      const history = hasHistory(question.qid);
      if (!history) return 'check_box_outline_blank';
      const blank = history.marked_alternative === '';
      if (blank) return 'check_box_outline_blank';
      if (history.scored) return 'check';
      return 'close';
    };

    const setIconColor = (question: Objective) => {
      const history = hasHistory(question.qid);
      const blank = history?.marked_alternative === '';
      if (blank || !history) return;
      return 'white';
    };

    const hasHistory = (qid: string) => {
      return refHistory.value.find((history) => history.qid === qid);
    };

    const setItemBg = (question: Objective) => {
      const history = hasHistory(question.qid);
      const blank = history?.marked_alternative === '';
      if (blank || !history) return {};
      if (history.scored) return { 'bg-positive': true, 'text-white': true };
      return { 'bg-negative': true, 'text-white': true };
    };

    const classObject = computed(() => {
      return (question: Objective) => {
        const history = hasHistory(question.qid);
        let notBlank = history?.marked_alternative !== '';
        if (!history) notBlank = false;
        return {
          'bg-positive': notBlank,
          'text-white': notBlank,
        };
      };
    });

    const scrollTo = (index: number) => {
      const elementId = `question-${index + 1}`;
      if (index === 0) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        return;
      }
      const anchor = window.document.getElementById(elementId);
      anchor?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    };

    const warnDisabled = () => {
      disabled.value = !disabled.value;
      const questionNav = document.getElementById('list-questions');
      const minimize = document.getElementById('expand-icon');
      if (questionNav && minimize) {
        if (!disabled.value) {
          questionNav.style.maxHeight = '530px';
          minimize.style.transform = 'rotate(0deg)';
        } else {
          questionNav.style.maxHeight = '0px';
          minimize.style.transform = 'rotate(180deg)';
        }
      }
    };

    const moveFab = (ev: TouchPanType) => {
      if (ev.isFinal) {
        if (fabPos.value[0] < 0) {
          fabPos.value[0] = 50;
        }
        if (fabPos.value[1] < 50) {
          fabPos.value[1] = 75;
        }
      }

      draggingFab.value = ev.isFirst !== true && ev.isFinal !== true;

      fabPos.value = [
        fabPos.value[0] - ev.delta.x,
        fabPos.value[1] + ev.delta.y,
      ];
    };
    return {
      isHistory,
      disabled,
      classObject,
      draggingFab,
      fabPos,
      resetFabPos,
      moveFab,
      setIconColor,
      warnDisabled,
      getIconName,
      setItemBg,
      scrollTo,
    };
  },
});
